import { AfterViewInit, Component, ElementRef, EventEmitter, HostBinding, Input, NgZone, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { forkJoin, interval, Subscription } from 'rxjs';
import { FlatPlatform } from '../../model/product/flat-platform.model';
import { combinedRating, Product, isPlatformPlanned, platformCtaLabel, platformCtaKind, sortPlatformsByCoverage, platformSku, platformListPrice, freeStoreOfferLabel, platformCtaLabels } from '../../model/product/product.model';
import { StoreService } from '../../service/store/store.service';
import { DirectDownloadService } from '../../service/store/direct-download.service';
import { CartPopupService } from '../../service/cart/cart-popup.service';
import { ShowcaseTheme } from '../product/theme-showcase/theme-showcase.component';
import { PopoverShape } from './popover-shape';
import { MonitorProviderService } from '../../service/provider/monitor-provider.service';
import { UtilService } from '../../service/util/util.service';
import { ThemeService } from '../../service/theme/theme.service';
import { DarkLightSettings } from '../../data/theme/theme.data';

/**
 * Full-width detail row for the themes page grid. Opens between the clicked
 * poster card's row and the next row (Google-Images style) and shows the
 * home's themes box (app-theme-showcase) for this one theme: no themes' dock,
 * this theme's platforms, its stats, its store button and price (owner
 * 2026-10-15). This component keeps the state the home keeps for its box.
 * Closes by clicking the same card again (no dedicated close button).
 */
@Component( {
  selector: 'app-theme-expander',
  templateUrl: './theme-expander.component.html',
  styleUrls: [ './theme-expander.component.scss' ],
} )
export class ThemeExpanderComponent implements OnChanges, AfterViewInit, OnDestroy {

  @Input() product: Product = undefined;
  @Input() category: string;
  // Themes-page platform filter (a platform name, or undefined). When set,
  // the expander lists ONLY that platform and pins the screenshot on it (no
  // auto-cycle) - the same contract as the poster card's input.
  @Input() selectedPlatformFilter?: string;
  // Themes-page pricing filter ('Free' | 'Paid' | undefined). When set, only
  // the platforms whose listing matches that pricing are shown. Themes with
  // no matching listing never reach the expander - the grid filter already
  // drops their cards.
  @Input() selectedPricingFilter?: string;
  /** the box in the page itself, no popover (the theme page's top box, owner 2026-10-15) */
  @Input() @HostBinding( 'class.inline' ) inline = false;

  /** the page's band behind the box (the tool pages' ground, owner 2026-10-15) in this theme's first colour */
  @HostBinding( 'style.--box-band-color' )
  get bandColor(): string | null {
    return this.inline ? this.product?.designTokens?.primaryColor || null : null;
  }
  /** the platform on show, by name, whenever it changes (the theme page keeps its reviews on it) */
  @Output() platformChange = new EventEmitter<string>();

  platforms: FlatPlatform[] = [];
  selectedPlatformIndex = 0;

  /** The box's theme: this one, its category as the theme page's link takes it. */
  active: ShowcaseTheme | null = null;
  get ctaLabels(): readonly string[] { return platformCtaLabels( this.product ); }
  /** While the pointer or the focus is in the box, or it is off screen: the platforms do not turn. */
  held = false;

  // Download/rating stats are fetched lazily, only when a card is expanded;
  // the grid itself never loads stats. A live theme always reports downloads,
  // so a 0 total is retried a few times before reading as unavailable.
  statsLoaded = false;
  actualDownloads = 0;
  rating?: number;
  private statsAttempt = 0;
  private readonly maxStatsAttempts = 3;
  private statsRetryTimer: any;

  private cycleSubscription?: Subscription;

  // Site dark/light MODE only - never the applied product skin: the box
  // takes it as the site's mode (siteDark).
  darkModeActive = false;

  /** The box as a popover pointing at the clicked card. */
  private readonly shape: PopoverShape;

  constructor( private host: ElementRef<HTMLElement>,
               private zone: NgZone,
               public utilService: UtilService,
               private monitorProviderService: MonitorProviderService,
               private themeService: ThemeService,
               private storeService: StoreService,
               private cartPopupService: CartPopupService,
               private directDownloadService: DirectDownloadService ) {
    this.shape = new PopoverShape( host, zone );
    this.themeService.darkLightSetting.subscribe( darkLightSetting => {
      // The explicit setting wins; only 'auto' falls back to the OS
      // preference (selectDarkLightByCSS reads prefers-color-scheme alone,
      // so by itself it would ignore a manually chosen mode).
      this.darkModeActive = darkLightSetting === DarkLightSettings.dark
        || ( darkLightSetting !== DarkLightSettings.light
             && this.themeService.selectDarkLightByCSS( false, true ) );
      if ( DarkLightSettings.auto === darkLightSetting ) {
        window.matchMedia( '(prefers-color-scheme: dark)' ).addEventListener( 'change', () => {
          this.darkModeActive = this.themeService.selectDarkLightByCSS( false, true );
        } );
      }
    } );
  }

  ngOnChanges( changes: SimpleChanges ): void {
    if ( !this.product || !( changes.product || changes.selectedPlatformFilter || changes.selectedPricingFilter ) ) {
      return;
    }
    this.resetForProduct();
    // another card of the same row moves the arrow
    if ( !this.inline ) {
      this.shape.schedule();
    }
  }

  ngAfterViewInit(): void {
    if ( !this.inline ) {
      this.shape.attach();
    }
  }

  ngOnDestroy(): void {
    this.stopCycle();
    this.clearStats();
    this.shape.destroy();
  }

  /** The total as it stands, no count-up (owner 2026-10-15). */
  get downloads(): string {
    return this.actualDownloads > 0 ? this.utilService.numberFormatter( this.actualDownloads ) : '';
  }

  get selectedPlatform(): FlatPlatform | null {
    return this.platforms[ this.selectedPlatformIndex ] ?? null;
  }

  get currentScreenshot(): any {
    return this.selectedPlatform?.screenshot ?? null;
  }

  /** "Add to cart" for a listing with a sku (paid or free), "Go to store" for one that links out, "Download now" for Rhino. */
  get ctaText(): string {
    const name = this.selectedPlatform?.name;
    return platformCtaLabel( this.product, name, this.storeService.availability( platformSku( this.product, name ) ) );
  }

  /** a store listing the store has said it cannot sell: the CTA is inert (Coming soon); it stays active while the store is still answering */
  get selectedStoreUnavailable(): boolean {
    const sku = platformSku( this.product, this.selectedPlatform?.name );
    return !!sku && !this.storeService.canAdd( sku );
  }

  /** "$1.99" when the selected listing is sold on the site (live catalogue price, else the list price), else null. */
  get selectedPriceLabel(): string | null {
    const name = this.selectedPlatform?.parentName || this.selectedPlatform?.name;
    const sku = platformSku( this.product, name );
    return sku ? this.storeService.priceLabel( sku, platformListPrice( this.product, name ) ) : freeStoreOfferLabel( this.product, name );
  }

  /** Whether the selected platform is only planned (CTA inactive, "Coming soon"). */
  get selectedPlatformPlanned(): boolean {
    return isPlatformPlanned( this.product, this.selectedPlatform?.name ) || this.selectedStoreUnavailable;
  }

  /** Pin a platform from its dock (stops the auto-cycle). */
  selectPlatform( index: number ): void {
    this.stopCycle();
    this.selectedPlatformIndex = index;
    this.emitPlatform();
  }

  private emitPlatform(): void {
    const name = this.selectedPlatform?.name;
    if ( name ) {
      // after this check: the theme page binds what it hears
      Promise.resolve().then( () => this.platformChange.emit( name ) );
    }
  }

  /** CTA for the selected platform - same routing as the theme page banner:
   *  a listing with a sku (paid or free) goes to the cart; Slack copies its
   *  colours, Rhino downloads its file; marketplace platforms open their
   *  listing. The click is recorded either way. */
  getSelected(): void {
    const plat = this.selectedPlatform;
    if ( !plat ) {
      return;
    }
    this.monitorProviderService.recordClick( this.product.key, plat.name ).subscribe( {
      error: ( err ) => console.error( 'Failed to record platform click:', err )
    } );

    // A listing sold on the site: Add to cart puts it in the cart and opens
    // the mini cart (a toast on a phone); the page stays.
    const sku = platformSku( this.product, plat.name );
    if ( sku ) {
      this.cartPopupService.add( sku, 'theme-expander' );
      return;
    }

    // the free listing outside the cart (2026-09-21, no theme/content page any more): Rhino
    if ( plat.downloadable ) {
      if ( platformCtaKind( this.product, plat.name ) === 'download' ) this.directDownloadService.downloadRhino( this.product, plat.name, plat.url );
      return;
    }

    window.open( plat.url, '_blank' );
  }

  private resetForProduct(): void {
    this.active = { product: this.product, category: ( this.category || '' ).toLowerCase() };

    // An active themes-page platform filter keeps EVERY platform pill visible
    // (hiding the rest was confusing); the filtered platform just opens
    // pre-selected, and the auto-cycle stays off (startCycle checks the
    // filter) so the shot holds on it.
    const platforms = this.flattenPlatforms();
    this.platforms = platforms;
    let startIndex = 0;
    if ( this.selectedPlatformFilter ) {
      const sel = this.selectedPlatformFilter.toLowerCase();
      const found = platforms.findIndex( p => p.name?.toLowerCase() === sel );
      if ( found >= 0 ) {
        startIndex = found;
      }
    }
    this.selectedPlatformIndex = startIndex;
    this.emitPlatform();
    this.startCycle();

    this.clearStats();
    this.statsLoaded = false;
    this.actualDownloads = 0;
    this.rating = undefined;
    this.statsAttempt = 0;
    if ( this.utilService.isExternalIdExist( this.product ) ) {
      this.fetchStats();
    } else {
      this.statsLoaded = true;
    }
  }

  /** Whether a platform listing matches the active pricing filter (the
   *  poster card's contract: per-listing `paid` flag, Free = non-paid). */
  private matchesPricing( platform: any ): boolean {
    if ( !this.selectedPricingFilter ) {
      return true;
    }
    return ( platform?.paid === true ) === ( this.selectedPricingFilter === 'Paid' );
  }

  /** Same flattening as the theme page banner: one entry per IDE/editor
   *  variant, each carrying its own screenshot (legacy positional backfill).
   *  Listings that miss the active pricing filter are skipped entirely. */
  private flattenPlatforms(): FlatPlatform[] {
    const out: FlatPlatform[] = [];
    if ( !this.product?.platforms ) {
      return out;
    }
    for ( const platform of sortPlatformsByCoverage( this.product.platforms ) ) {
      if ( !this.matchesPricing( platform ) ) {
        continue;
      }
      const info: any = platform.info;
      const screenshot = platform.screenshot;
      if ( info?.supportedPlatforms?.length > 0 ) {
        for ( const sp of info.supportedPlatforms ) {
          out.push( {
            name: sp.name,
            logo: sp.logo,
            url: platform.url,
            downloadable: info.downloadable,
            screenshot,
            parentName: info.name,
            category: sp.category ?? info.category,
          } );
        }
      } else if ( info ) {
        out.push( {
          name: info.name,
          logo: info.logo,
          url: platform.url,
          downloadable: info.downloadable,
          screenshot,
          category: info.category,
        } );
      }
    }
    return out;
  }

  private startCycle(): void {
    this.stopCycle();
    // No cycling under a platform filter (the shot is pinned), with a single
    // platform, or for reduced-motion users.
    if ( this.selectedPlatformFilter || this.platforms.length <= 1 || this.prefersReducedMotion() ) {
      return;
    }
    this.cycleSubscription = interval( 6000 ).subscribe( () => {
      if ( this.held ) {
        return;
      }
      this.selectedPlatformIndex = ( this.selectedPlatformIndex + 1 ) % this.platforms.length;
      this.emitPlatform();
    } );
  }

  private stopCycle(): void {
    this.cycleSubscription?.unsubscribe();
    this.cycleSubscription = undefined;
  }

  private fetchStats(): void {
    forkJoin( [ this.monitorProviderService.getStats( this.product.key ), this.storeService.reviews() ] ).subscribe( {
      next: ( [ results, store ] ) => {
        let total = 0;
        ( results || [] ).forEach( ( platformData: any ) => {
          // Chrome reports installs as downloads once the monitor has them; its weekly
          // "users" are the same people, so they only stand in while downloads are 0.
          total += platformData.downloads || platformData.users || 0;
        } );
        // one rating over every marketplace and the site's own buyers
        this.rating = combinedRating( results, store?.products?.[ this.product.key ] ) || undefined;

        if ( total === 0 ) {
          this.retryStats();
          return;
        }

        this.actualDownloads = total;
        this.statsLoaded = true;
      },
      error: () => this.retryStats(),
    } );
  }

  private retryStats(): void {
    this.statsAttempt++;
    if ( this.statsAttempt < this.maxStatsAttempts ) {
      this.statsRetryTimer = setTimeout( () => this.fetchStats(), 800 );
      return;
    }
    // Out of retries: surface N/A instead of a spinner forever.
    this.statsLoaded = true;
  }

  private clearStats(): void {
    if ( this.statsRetryTimer ) {
      clearTimeout( this.statsRetryTimer );
      this.statsRetryTimer = undefined;
    }
  }

  private prefersReducedMotion(): boolean {
    return typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;
  }
}
